import type { Entry } from "contentful";
import { Suspense, useRef, useState } from "react";
import { Await, Link, useLoaderData } from "react-router";

import { SWR } from "~/.server/caching.server";
import { getClient } from "~/contentful/contentful.server";
import { deferDataRequest } from "~/lib/utils-server/http.server";
import { getPageMeta } from "~/seo/servers/seo.server";

import {
  CheckCircledIcon,
  ChevronRightIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";

import Breadcrumbs from "~/components/breadcrumbs";
import { AcceptedPayments } from "~/components/footer/footer";
import { Alert, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import type { TypeFooterSkeleton } from "~/contentful/compiled/TypeFooter";
import { FOOTER_CONTENT } from "~/contentful/contents";
import { useURL } from "~/contexts";
import { cn } from "~/lib/ui";
import { useFormatPrice } from "~/shop/utils/price";

import { getOrderTotal } from "./checkout/utils/totals";
import { useBasket } from "./resources/basket";
import * as BasketLineItem from "./resources/components/basket-line-item";
import { PriceSummaryPartial } from "./resources/components/price-summary-partial";
import { WishlistCTA, useWishlist } from "./resources/wishlist";

export const loader = async ({ context }: LoaderArgs) => {
  const client = getClient();
  const [swr] = SWR.stable("footer-data-payment", [
    "footer",
    "data",
    "contentful",
    "payment",
  ]);

  /* For GA4 */
  const analytics = {
    action: "page_view",
    category: "bag_page",
    label: "bag_page",
    value: "bag_page",
  };

  /* For SEO */
  const seo = getPageMeta({
    type: "cart",
    options: {
      title: "Bag page",
      description: "The Bag page of Spark* store",
    },
    robots: {
      noFollow: false,
      noIndex: true,
    },
  });

  const [paymentTypesData] = await deferDataRequest(
    swr(async () => {
      return await client.getEntry<TypeFooterSkeleton>(FOOTER_CONTENT);
    }, []),
  );
  return { paymentTypesData, seo, analytics };
};

const BasketPage = () => {
  const { basket } = useBasket() ?? {};
  const { isInList } = useWishlist() ?? {};
  const url = useURL();
  const formatPrice = useFormatPrice();
  const { paymentTypesData } = useLoaderData<typeof loader>();
  const boundryRef = useRef<HTMLDivElement>(null!);
  const [, setFlag] = useState<boolean>();

  return (
    <>
      <div className="px-6 pt-3 md:px-[33px] xl:px-[41px] 2xl:px-[96px]">
        <Breadcrumbs pageName="Shopping Bag" />
      </div>
      <div className="px-6 pb-4 pt-1 md:px-[33px] xl:px-[142px] 2xl:px-[242px]">
        {!basket && <Skeletons size={4} />}
        {basket && (
          <Suspense fallback={<Skeletons size={4} />}>
            <Await resolve={basket}>
              {basket => {
                const productQty = basket?.productItems?.reduce(
                  (acc, currentValue) => acc + (currentValue?.quantity || 0),
                  0,
                );
                return (
                  <>
                    {!basket?.productItems?.length && (
                      <div className="flex flex-col sm:flex-row">
                        <div className="basis-full sm:basis-4/6 xl:basis-full">
                          <div className="w-full flex-row items-start justify-between border-b border-b-gray-200 pb-2 pt-3">
                            <h1 className="flex-1 text-2xl font-semibold text-contrast-black">
                              Shopping Bag ({basket?.productItems?.length || 0})
                            </h1>
                          </div>
                          <Alert
                            className="my-3 block sm:hidden"
                            variant="info"
                          >
                            <InfoCircledIcon className="h-4 w-4" />
                            <AlertTitle className="text-sm font-normal text-contrast-black">
                              <p>Spend a further £5 to qualify for:</p>
                              <p>FREE Delivery</p>
                            </AlertTitle>
                          </Alert>
                          <Alert
                            className="mt-3 [&>svg]:top-3.5"
                            variant="neutral"
                          >
                            <InfoCircledIcon className="h-4 w-4" />
                            <AlertTitle className="text-sm font-normal text-empty">
                              Empty
                            </AlertTitle>
                          </Alert>
                          <Button
                            as={Link}
                            to={url("/")}
                            prefetch="intent"
                            variant="outline"
                            className="my-3 w-full text-base text-neutral-11 hover:no-underline xl:w-[155px]"
                          >
                            Keep Shopping
                          </Button>
                        </div>
                        <div className="ml-3 hidden basis-full sm:block sm:basis-2/6 xl:ml-8 xl:max-w-[312px] xl:basis-full 2xl:ml-12">
                          <div className="items-start justify-between border-b border-b-gray-200 pb-2 pt-3">
                            <h2 className="text-dark-on-light text-left text-2xl font-semibold">
                              Order Summary
                            </h2>
                          </div>
                          <Alert
                            className="my-3 block [&>svg]:top-3.5"
                            variant="info"
                          >
                            <InfoCircledIcon className="h-4 w-4" />
                            <AlertTitle className="text-sm font-normal text-contrast-black">
                              <p>Spend a further £5 to qualify for:</p>
                              <p>FREE Delivery</p>
                            </AlertTitle>
                          </Alert>
                        </div>
                      </div>
                    )}
                    {basket?.productItems && (
                      <div className="flex flex-col sm:flex-row">
                        <div className="basis-full sm:mx-0">
                          <div className="w-full flex-row items-start justify-between border-b border-b-gray-200 py-3">
                            <h1 className="text-dark-on-light flex text-left text-2xl font-semibold">
                              <span className="flex-1 text-2xl">
                                Shopping Bag ({productQty})
                              </span>
                              <span className="self-end text-lg font-normal sm:hidden">
                                {formatPrice(getOrderTotal(basket))}
                              </span>
                            </h1>
                          </div>
                          <div className="block border-b border-b-gray-200 py-4 sm:mx-4 sm:hidden">
                            <Alert variant="info">
                              <InfoCircledIcon className="text-dark-on-light h-4 w-4" />
                              <AlertTitle className="text-dark-on-light text-sm font-normal">
                                <p>Spend a further £5 to qualify for:</p>
                                <p>FREE Delivery</p>
                              </AlertTitle>
                            </Alert>

                            <Alert className="mt-3" variant="success">
                              <CheckCircledIcon className="text-dark-on-light h-4 w-4" />
                              <AlertTitle className="text-dark-on-light text-sm font-normal">
                                <p>Your order qualifies for:</p>
                                <p>FREE Click & Collect</p>
                              </AlertTitle>
                            </Alert>
                          </div>
                          <section
                            aria-labelledby="cart-action"
                            className="flex items-center justify-center sm:hidden"
                          >
                            <Button
                              as={Link}
                              prefetch="intent"
                              to={url("/checkout")}
                              className="mb-5 mt-3 w-full hover:no-underline sm:mx-5"
                            >
                              <span className="px-2 text-base font-semibold text-neutral-1">
                                Go to Checkout
                              </span>
                              <ChevronRightIcon className="h-[18px] w-[18px]" />
                            </Button>
                          </section>
                          <ul className="flex-col overflow-x-auto sm:h-[650px]">
                            {(basket?.productItems ?? [])
                              .concat()
                              .reverse()
                              .map(lineItem => (
                                <BasketLineItem.Root
                                  boundryRef={boundryRef}
                                  key={lineItem.itemId}
                                  lineItem={lineItem}
                                  closeCartDrawer={() => {}}
                                >
                                  {isStale => (
                                    <li
                                      key={lineItem.itemId}
                                      className={cn(
                                        "flex flex-row gap-4 pt-5",
                                        isStale ? "opacity-50" : "opacity-100",
                                      )}
                                    >
                                      <BasketLineItem.ProductImage />
                                      <div className="flex-1">
                                        <div className="flex flex-row justify-between">
                                          <div className="sm:basis-2/4 lg:basis-11/12">
                                            <BasketLineItem.ProductName />
                                            <BasketLineItem.ProductVariants />
                                            <div className="block text-sm md:hidden">
                                              <BasketLineItem.ProductQuantity />
                                            </div>
                                            <button
                                              className="w-full"
                                              onClick={() => {
                                                setFlag(prev => !prev);
                                              }}
                                            >
                                              <WishlistCTA
                                                productId={
                                                  lineItem.productId ?? ""
                                                }
                                                className="flex"
                                                iconHeight={10}
                                                iconWidth={10}
                                              >
                                                <span className="px-1 text-xs">
                                                  {isInList &&
                                                  isInList(
                                                    lineItem.productId ?? "",
                                                  )
                                                    ? "Remove from wishlist"
                                                    : "Save to wishlist"}
                                                </span>
                                              </WishlistCTA>
                                            </button>
                                            <BasketLineItem.RemoveProduct className="p-0" />
                                          </div>
                                          <div className="hidden basis-1/4 self-center text-sm sm:hidden md:block lg:flex lg:justify-end">
                                            <BasketLineItem.ProductQuantity />
                                          </div>
                                          <div className="basis-1/12 text-right text-sm sm:basis-1/4">
                                            <BasketLineItem.ProductPrice className="flex flex-col" />
                                          </div>
                                        </div>
                                      </div>
                                    </li>
                                  )}
                                </BasketLineItem.Root>
                              ))}
                          </ul>
                        </div>
                        <div className="w-full basis-full sm:ml-3 sm:max-w-[312px] xl:ml-8">
                          <div className="items-start justify-between border-b border-b-gray-200 py-3">
                            <h2 className="text-dark-on-light text-left text-2xl font-semibold">
                              Order Summary
                            </h2>
                          </div>
                          <div className="hidden py-4 sm:block">
                            <Alert className="[&>svg]:top-4" variant="info">
                              <InfoCircledIcon className="text-dark-on-light h-3.25 w-3.25" />
                              <AlertTitle className="text-dark-on-light text-sm font-normal">
                                <p>Spend a further £5 to qualify for:</p>
                                <p>FREE Delivery</p>
                              </AlertTitle>
                            </Alert>

                            <Alert
                              className="mt-3 [&>svg]:top-4"
                              variant="success"
                            >
                              <CheckCircledIcon className="text-dark-on-light h-3.25 w-3.25" />
                              <AlertTitle className="text-dark-on-light text-sm font-normal">
                                <p>Your order qualifies for:</p>
                                <p>FREE Click & Collect</p>
                              </AlertTitle>
                            </Alert>
                          </div>

                          <PriceSummaryPartial summaryData={basket} />

                          <section
                            aria-labelledby="cart-action"
                            className="flex items-center justify-center"
                          >
                            <Button
                              as={Link}
                              prefetch="intent"
                              to={url("/checkout")}
                              className="mt-3 w-full hover:no-underline"
                              data-testid="go-to-checkout-button"
                            >
                              <span className="px-2 text-base font-semibold text-neutral-1">
                                Go to Checkout
                              </span>
                              <ChevronRightIcon className="h-[18px] w-[18px]" />
                            </Button>
                          </section>
                          <div className="mx-4 my-6 items-center justify-center text-center">
                            <Suspense fallback={null}>
                              <Await resolve={paymentTypesData}>
                                {paymentTypesData => (
                                  <AcceptedPayments
                                    payments={
                                      (
                                        paymentTypesData as Entry<
                                          TypeFooterSkeleton,
                                          "WITHOUT_UNRESOLVABLE_LINKS",
                                          string
                                        >
                                      )?.fields?.paymentTypes?.fields.collection
                                    }
                                    label="Payment Types"
                                    className="flex flex-col py-2 text-center"
                                    labelClassName="pb-3 text-contrast-black text-xs font-light"
                                  />
                                )}
                              </Await>
                            </Suspense>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              }}
            </Await>
          </Suspense>
        )}
      </div>
    </>
  );
};

const Skeletons = ({ size = 20 }: { size?: number }) => {
  return new Array(size).fill(0).map((_, i) => (
    <div key={i} className="flex h-[112px] w-full pb-2">
      <Skeleton className="h-full w-full" />
    </div>
  ));
};
export default BasketPage;
